/**
 * Contact Us form schema (COM-735) — ONE schema, run by the RVF form in the
 * browser and again by `/resources/contact-us` on the server, so the two can
 * never disagree about what is submittable.
 *
 * Source of truth is the live SFRA content asset `about-fsa-store-contactus`
 * (`form.c-contactus`, read on fsa.devhec.com 2026-10-13) and its client
 * script `org_hec/cartridge/client/default/js/contactUs/contactUs.js` — NOT
 * the base `contactUs.isml` / `contactUs.properties`, whose subject list
 * (GI/OS/MA/O) is a different form entirely (COM-218 spike).
 *
 * Deliberate deltas from SFRA, both from the COM-735 acceptance criteria:
 *  - Order Number is ALWAYS required. SFRA required it only for "Order Status"
 *    / "Returns & Exchanges" (plus a dead "Delivery Issue" option); the ticket
 *    makes it unconditional, which is what the "…or N/A for other inquiries"
 *    copy already asks of the shopper.
 *  - The server validates. SFRA's `ContactUs-Subscribe` trusts `req.form`
 *    outright, so anything that skipped the browser script was emailed as-is.
 */
import { z } from "zod";

import { withZod } from "@rvf/zod";

/**
 * Subject options, in the live order. Value equals label — SFRA emails the
 * value verbatim as `"<SiteID> Contact Us: <value>"`, and the support inbox
 * routes on that subject line, so these strings are a contract.
 */
export const CONTACT_US_SUBJECTS = [
  "Order Status",
  "Returns & Exchanges",
  "Pricing or Promotion Inquiry",
  "Product Inquiry",
  "Other",
] as const;

/** `contactUs.js:91`, verbatim — the live format check. */
export const CONTACT_US_EMAIL_PATTERN = /^[\w.%+-]+@[\w.-]+\.[\w]{2,6}$/;

/**
 * `maxlength` on the live inputs — production parity, not new validation (the
 * AC's "no character limits" means none beyond these). Description has none in
 * production. The UI cannot exceed them, so the server re-applying them reuses
 * the field's copy rather than inventing a length message nobody can reach.
 */
export const CONTACT_US_MAX_LENGTH = {
  name: 50,
  email: 50,
  orderNumber: 30,
} as const;

/**
 * Server-only ceiling on Description. Production has no limit and the field
 * shows none (AC: "no character limits"); this only stops a scripted POST from
 * mailing megabytes to the support inbox. No shopper types 10k characters.
 */
export const CONTACT_US_DESCRIPTION_SERVER_MAX = 10_000;

/** Ticket / `contactUs.js:4-25` copy, verbatim. */
export const CONTACT_US_ERRORS = {
  name: "Please enter full name",
  email: "Please enter valid email",
  subject: "Please select subject",
  orderNumber: 'Please enter order number or "N/A" for other inquiries.',
  description: "Please enter description.",
} as const;

/**
 * Required = non-empty, exactly as `contactUs.js` checks it (no trim): the
 * ticket forbids validation beyond production's. Length caps sit on the
 * inputs (`maxLength`); the server re-applies them so a scripted POST cannot
 * exceed what the form allows.
 */
const required = (message: string, max?: number) => {
  const base = z.string({ required_error: message }).min(1, message);
  return max === undefined ? base : base.max(max, message);
};

export const contactUsSchema = z.object({
  name: required(CONTACT_US_ERRORS.name, CONTACT_US_MAX_LENGTH.name),
  email: z
    .string({ required_error: CONTACT_US_ERRORS.email })
    .max(CONTACT_US_MAX_LENGTH.email, CONTACT_US_ERRORS.email)
    .regex(CONTACT_US_EMAIL_PATTERN, CONTACT_US_ERRORS.email),
  // "Please Select" posts `""`, so the enum rejects it with the same copy.
  subject: z.enum(CONTACT_US_SUBJECTS, {
    errorMap: () => ({ message: CONTACT_US_ERRORS.subject }),
  }),
  orderNumber: required(
    CONTACT_US_ERRORS.orderNumber,
    CONTACT_US_MAX_LENGTH.orderNumber,
  ),
  description: required(
    CONTACT_US_ERRORS.description,
    CONTACT_US_DESCRIPTION_SERVER_MAX,
  ),
});

export const contactUsValidator = withZod(contactUsSchema);
